/**
 * Глобальный сервис модалок-подтверждений. Используется как альтернатива
 * нативному `window.confirm()` — у нас в проекте нативные диалоги запрещены
 * (нет стилизации, блокируют поток, плохой UX).
 *
 * Usage:
 *   const ok = await useConfirmDialog().ask({
 *     title: "Удалить подбор?",
 *     message: "Это действие нельзя отменить",
 *     okText: "Удалить",
 *     okType: "danger",
 *   });
 *   if (!ok) return;
 *
 * Один компонент `AppConfirmDialog` смонтирован глобально в `App.vue` —
 * читает текущее состояние из этого store и рендерит DxPopup. Очередь
 * не поддерживаем: одно подтверждение за раз. Если открыт второй ask()
 * пока первый не закрыт — он встанет «в ожидание», получит свой шанс
 * после того, как пользователь ответит на текущий.
 */

import { defineStore } from "pinia";
import { ref } from "vue";

export type ConfirmOkType = "default" | "danger" | "success";

export interface ConfirmRequest {
  title: string;
  message: string;
  okText?: string;
  cancelText?: string;
  /** Стилистика основной кнопки. `danger` — для удаления / архива. */
  okType?: ConfirmOkType;
}

interface QueuedRequest extends ConfirmRequest {
  resolve: (value: boolean) => void;
}

export const useConfirmDialog = defineStore("confirmDialog", () => {
  const current = ref<QueuedRequest | null>(null);
  const queue: QueuedRequest[] = [];

  function ask(req: ConfirmRequest): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const item: QueuedRequest = { ...req, resolve };
      if (current.value) {
        queue.push(item);
      } else {
        current.value = item;
      }
    });
  }

  function confirm() {
    if (!current.value) return;
    current.value.resolve(true);
    advance();
  }

  function cancel() {
    if (!current.value) return;
    current.value.resolve(false);
    advance();
  }

  function advance() {
    current.value = queue.shift() ?? null;
  }

  return { current, ask, confirm, cancel };
});
